import { useState } from "react";
import { toast } from "sonner";
import { Phone, Heart, Snowflake, Music, Dumbbell, Pen, Lock, Send, LifeBuoy, Headphones } from "lucide-react";
import BreathingCircle from "@/components/BreathingCircle";
import { api } from "@/lib/api";

const emergencyNumbers = [
  {
    code: "3114",
    title: "Numéro national de prévention du suicide",
    subtitle: "Gratuit, anonyme, 24h/24 — 7j/7",
    testId: "emergency-3114",
  },
  {
    code: "114",
    title: "Urgences par SMS / message (sourds, malentendants ou en silence)",
    subtitle: "SMS gratuits, 24h/24",
    testId: "emergency-114",
  },
  {
    code: "15",
    title: "SAMU — urgences médicales et psychiatriques",
    subtitle: "Appel gratuit, immédiat",
    testId: "emergency-15",
  },
  {
    code: "112",
    title: "Numéro d'urgence européen",
    subtitle: "Depuis toute l'Union européenne",
    testId: "emergency-112",
  },
];

const groundingSteps = [
  { n: 5, label: "choses que je vois", color: "text-orange-300" },
  { n: 4, label: "choses que je touche", color: "text-emerald-300" },
  { n: 3, label: "sons que j'entends", color: "text-sky-300" },
  { n: 2, label: "odeurs que je perçois", color: "text-rose-300" },
  { n: 1, label: "goût dans ma bouche", color: "text-amber-300" },
];

const alternatives = [
  { Icon: Snowflake, title: "Tenir un glaçon", text: "La sensation forte ramène le corps dans le présent, sans se faire mal." },
  { Icon: Pen, title: "Gribouiller très fort", text: "Un stylo rouge sur ta peau, du papier déchiré, un dessin rageur — fais sortir ce qui pèse." },
  { Icon: Dumbbell, title: "20 pompes / 50 jumping jacks", text: "L'effort décharge l'adrénaline. Après 60 secondes, le pic d'angoisse redescend." },
  { Icon: Music, title: "Une chanson que tu aimes", text: "Fort, dans un casque. Chante, pleure, bouge. La musique traverse la tête." },
];

export default function SosSection() {
  const [form, setForm] = useState({ nickname: "", contact: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim() || form.message.trim().length < 3) {
      toast.error("Écris-nous quelques mots, on te lira.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/sos/messages", form);
      toast.success("Message reçu. Tu n'es pas seul·e, on est là.");
      setForm({ nickname: "", contact: "", message: "" });
    } catch {
      toast.error("Envoi impossible pour l'instant. Les numéros ci-dessus restent joignables.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="sos"
      data-testid="sos-section"
      className="relative py-20 lg:py-28 border-y border-white/5"
      style={{
        background:
          "radial-gradient(900px 500px at 20% 10%, rgba(249, 115, 22, 0.08), transparent 60%), #0A1020",
      }}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <span className="em-eyebrow flex items-center gap-2">
              <LifeBuoy className="w-3.5 h-3.5" /> Priorité absolue
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              SOS & Soutien — tu n'es pas seul·e.
            </h2>
            <p className="mt-4 text-slate-300 max-w-2xl">
              Si c'est très dur maintenant, respire une fois, appelle un des numéros gratuits
              ci-dessous, puis reviens vers nous. On traverse ça ensemble.
            </p>
          </div>
        </div>

        {/* Emergency numbers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {emergencyNumbers.map((n) => (
            <a
              key={n.code}
              href={`tel:${n.code}`}
              data-testid={n.testId}
              className="em-card p-6 flex items-center gap-5 hover:border-orange-500/50"
            >
              <div className="w-16 h-16 shrink-0 rounded-2xl bg-orange-500/15 border border-orange-500/30 grid place-items-center">
                <span className="text-2xl font-bold text-orange-300 font-[Outfit]">{n.code}</span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-white font-semibold">
                  <Phone className="w-4 h-4 text-emerald-400" /> {n.title}
                </div>
                <p className="text-sm text-slate-400 mt-1">{n.subtitle}</p>
              </div>
            </a>
          ))}
        </div>

        {/* Anti-crisis kit */}
        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 em-card p-8 flex flex-col items-center">
            <span className="em-eyebrow">Kit anti-crise · Respiration</span>
            <h3 className="mt-2 text-2xl font-semibold text-white font-[Outfit]">
              Respire avec moi
            </h3>
            <div className="mt-8">
              <BreathingCircle />
            </div>
          </div>

          <div className="lg:col-span-7 em-card p-8">
            <span className="em-eyebrow">Méthode 5-4-3-2-1 · Ancrage</span>
            <h3 className="mt-2 text-2xl font-semibold text-white font-[Outfit]">
              Reviens dans le présent, sens par sens.
            </h3>
            <p className="mt-3 text-slate-300">
              Quand la tête tourne trop vite, nomme à voix haute (ou dans ta tête) :
            </p>
            <ul className="mt-6 space-y-3" data-testid="grounding-list">
              {groundingSteps.map((s) => (
                <li
                  key={s.n}
                  className="flex items-center gap-4 p-3 rounded-xl bg-white/5 border border-white/5"
                >
                  <span className={`text-3xl font-bold w-10 text-center ${s.color} font-[Outfit]`}>
                    {s.n}
                  </span>
                  <span className="text-slate-200">{s.label}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm text-slate-400">
              Prends ton temps. Cet exercice déconnecte le mental de la boucle d'angoisse.
            </p>
          </div>
        </div>

        {/* Alternatives */}
        <div className="mt-10">
          <h3 className="text-2xl font-semibold text-white font-[Outfit]">
            Alternatives physiques à l'envie de se faire mal
          </h3>
          <p className="mt-2 text-slate-400 max-w-3xl">
            L'idée : décharger l'intensité du moment sans te blesser. Choisis-en une et tiens 2 minutes.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {alternatives.map(({ Icon, title, text }) => (
              <div key={title} className="em-card p-6" data-testid={`alt-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
                  <Icon className="w-5 h-5 text-emerald-300" />
                </div>
                <h4 className="mt-4 text-white font-semibold">{title}</h4>
                <p className="mt-2 text-sm text-slate-400">{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Spotify Playlist Anti-Crise */}
        <div className="mt-10 em-card p-6 lg:p-8" data-testid="playlist-card">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
              <Headphones className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-2xl font-semibold text-white font-[Outfit]">
                Playlist anti-crise
              </h3>
              <p className="mt-2 text-slate-300 max-w-2xl">
                Mets un casque, baisse les lumières, laisse la musique s'occuper du reste. Reste
                avec nous le temps qu'il faut.
              </p>
            </div>
          </div>
          <div className="mt-6 rounded-2xl overflow-hidden border border-white/10 bg-black/40">
            <iframe
              title="Playlist anti-crise — Peaceful Piano"
              data-testid="spotify-embed"
              src="https://open.spotify.com/embed/playlist/37i9dQZF1DX4sWSpwq3LiO?utm_source=generator&theme=0"
              width="100%"
              height="352"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className="block w-full"
            />
          </div>
        </div>

        {/* Confidential form */}
        <div className="mt-14 em-card p-8 lg:p-10">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-orange-500/15 border border-orange-500/30 grid place-items-center">
              <Lock className="w-5 h-5 text-orange-300" />
            </div>
            <div>
              <h3 className="text-2xl font-semibold text-white font-[Outfit]">
                Un message confidentiel
              </h3>
              <p className="mt-2 text-slate-300 max-w-2xl">
                Lu uniquement par l'équipe de modération. Tu peux rester anonyme. Laisse un
                contact seulement si tu veux une réponse.
              </p>
            </div>
          </div>

          <form onSubmit={submit} className="mt-6 grid gap-4" data-testid="sos-form">
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                value={form.nickname}
                onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                placeholder="Pseudo (facultatif)"
                data-testid="sos-nickname"
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500 focus:border-orange-500/60"
                maxLength={60}
              />
              <input
                type="text"
                value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                placeholder="Email / Discord (facultatif)"
                data-testid="sos-contact"
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500 focus:border-orange-500/60"
                maxLength={150}
              />
            </div>
            <textarea
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Dis-nous ce que tu traverses. On lira sans juger."
              data-testid="sos-message"
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500 focus:border-orange-500/60"
              maxLength={4000}
              required
            />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-slate-500 flex items-center gap-2">
                <Heart className="w-3.5 h-3.5 text-rose-400" />
                Si tu es en danger immédiat, appelle le 15 ou le 3114.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="sos-submit"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full"
              >
                <Send className="w-4 h-4" />
                {submitting ? "Envoi…" : "Envoyer en confidentiel"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
